import { KeycloakService } from 'keycloak-angular';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Authentication } from 'src/app/helpers/authentication';
import { PersonaService } from 'src/app/services/persona.service';
import { EmpleadoService } from 'src/app/services/empleado.service';
import { environment } from 'src/environments/environment';
import { ThemeService } from 'src/app/core/services/theme.service';
import { AuthStateService } from 'src/app/core/services/auth-state.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit {

  private readonly maxAvatarFileSize = Math.floor(10 * 1024 * 1024);
  private readonly minAvatarDimension = 256;
  private readonly normalizedAvatarDimension = 512;

  @ViewChild('profileAvatarInput')
  private readonly profileAvatarInput!: ElementRef<HTMLInputElement>;

  public ofertName: UntypedFormControl;
  public authentication: Authentication = new Authentication();
  public empleadoRoleName: string = '';

  constructor(@Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly keycloakService: KeycloakService,
    private readonly personaService: PersonaService,
    private readonly empleadoService: EmpleadoService,
    public themeService: ThemeService,
    private readonly authState: AuthStateService) { }

  ngOnInit(): void {
    this.ofertName = new UntypedFormControl("", Validators.maxLength(20));
    this.authState.userDataAuth = this.authentication.getUserDataAuth();
    this.loadProfileAvatarFromDb();
    this.loadEmpleadoRole();
  }

  public getUserDataAuth(): any {
    return this.authState.userDataAuth;
  }


  public clearFilter() {
    this.ofertName.reset();
    this.router.navigate(["/offerts"], { queryParams: { ofertName: "all" } });
  }

  public findOfert(): void {
    this.router.navigate(["/offerts"], { queryParams: { ofertName: this.ofertName.value === "" ? "all" : this.ofertName.value } });
  }

  /**
  * Sidebar toggle on hamburger button click
  */
  toggleSidebar(e: Event) {
    e.preventDefault();
    this.document.body.classList.toggle('sidebar-open');
  }

  /**
   * Logout
   */
  onLogout(e: Event) {
    this.authentication.singout();
    this.keycloakService.logout(environment.keycloack.urlRedirectLogout);
  }

  goEmpployee() {
    this.router.navigate(["/employees"]);
  }

  /**
   * Get user initials for avatar fallback
   */
  getUserInitials(): string {
    const userData = this.getUserDataAuth();
    const nombre = userData?.nombre || '';

    if (!nombre) return 'U';

    const words = nombre.trim().split(/\s+/);

    if (words.length >= 2) {
      // Nombre completo: primera letra del nombre y apellido
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      // Solo un nombre: primeras dos letras
      return nombre.substring(0, 2).toUpperCase();
    }
  }

  /**
   * Get avatar color based on user name hash
   */
  getAvatarColor(): string {
    const userData = this.getUserDataAuth();
    const nombre = userData?.nombre || 'User';

    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
      '#98D8C8', '#6C5CE7', '#A29BFE', '#FD79A8',
      '#FDCB6E', '#6C5CE7', '#00B894', '#E17055'
    ];

    let hash = 0;
    for (let i = 0; i < nombre.length; i++) {
      hash = (nombre.codePointAt(i) ?? 0) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Get user primary role for display
   */
  getUserRole(): string {
    if (this.empleadoRoleName) {
      return this.empleadoRoleName;
    }
    const userData = this.getUserDataAuth();
    const roles: string[] = userData?.auth || [];
    return roles.length > 0 ? roles[0] : '';
  }

  private loadEmpleadoRole(): void {
    const userData = this.getUserDataAuth();
    if (userData?.auth?.length) {
      this.empleadoRoleName = userData.auth[0];
    }
  }

  /**
   * Check if user has Google OAuth avatar
   */
  hasGoogleAvatar(): boolean {
    const userData = this.getUserDataAuth();
    return !!(userData?.urlImg && userData.urlImg !== '');
  }

  openAvatarFilePicker(): void {
    this.profileAvatarInput?.nativeElement?.click();
  }

  onProfileAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type?.startsWith('image/')) {
      alert('El archivo debe ser una imagen válida.');
      this.clearProfileAvatarInput();
      return;
    }

    if (file.size > this.maxAvatarFileSize) {
      alert('La imagen no puede superar los 10,0 MB.');
      this.clearProfileAvatarInput();
      return;
    }

    this.prepareAvatarImage(file)
      .then(({ dataUrl, base64, contentType }) => {
        this.personaService.updateProfileAvatar({ foto: base64, fotoContentType: contentType }).subscribe({
          next: () => {
            this.updateUserAvatarLocally(dataUrl);
            this.clearProfileAvatarInput();
          },
          error: () => {
            alert('No fue posible guardar la foto de perfil.');
            this.clearProfileAvatarInput();
          }
        });
      })
      .catch((error: Error) => {
        if (error.message === 'AVATAR_TOO_SMALL') {
          alert(`La imagen debe tener mínimo ${this.minAvatarDimension}x${this.minAvatarDimension} píxeles para verse nítida.`);
        } else {
          alert('No fue posible procesar la imagen seleccionada.');
        }
        this.clearProfileAvatarInput();
      });
  }

  private prepareAvatarImage(file: File): Promise<{ dataUrl: string; base64: string; contentType: string }> {
    return this.readFileAsDataUrl(file)
      .then(dataUrl => this.loadImage(dataUrl))
      .then(image => {
        if (image.naturalWidth < this.minAvatarDimension || image.naturalHeight < this.minAvatarDimension) {
          throw new Error('AVATAR_TOO_SMALL');
        }

        const canvas = document.createElement('canvas');
        canvas.width = this.normalizedAvatarDimension;
        canvas.height = this.normalizedAvatarDimension;

        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('CANVAS_CONTEXT_NOT_AVAILABLE');
        }

        const cropSize = Math.min(image.naturalWidth, image.naturalHeight);
        const sourceX = Math.floor((image.naturalWidth - cropSize) / 2);
        const sourceY = Math.floor((image.naturalHeight - cropSize) / 2);

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(
          image,
          sourceX,
          sourceY,
          cropSize,
          cropSize,
          0,
          0,
          this.normalizedAvatarDimension,
          this.normalizedAvatarDimension
        );

        const outputContentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const normalizedDataUrl = outputContentType === 'image/jpeg'
          ? canvas.toDataURL(outputContentType, 0.95)
          : canvas.toDataURL(outputContentType);

        return {
          dataUrl: normalizedDataUrl,
          base64: normalizedDataUrl.split(',')[1] ?? '',
          contentType: outputContentType
        };
      });
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string) || '');
      reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
      reader.readAsDataURL(file);
    });
  }

  private loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('IMAGE_LOAD_ERROR'));
      image.src = dataUrl;
    });
  }

  clearProfileAvatarInput(): void {
    if (this.profileAvatarInput?.nativeElement) {
      this.profileAvatarInput.nativeElement.value = '';
    }
  }

  private loadProfileAvatarFromDb(): void {
    const userData = this.getUserDataAuth() || {};
    const customAvatar = this.authentication.getCustomAvatar(userData.correo || '');
    if (customAvatar) {
      this.updateUserAvatarLocally(customAvatar);
      return;
    }

    this.personaService.getProfileAvatar().subscribe({
      next: response => {
        const avatarDataUrl = this.mapAvatarResponseToDataUrl(response);
        if (avatarDataUrl) {
          this.updateUserAvatarLocally(avatarDataUrl);
        }
      },
      error: () => {}
    });
  }

  private mapAvatarResponseToDataUrl(response: any): string {
    const foto = response?.foto;
    const fotoContentType = response?.fotoContentType;
    if (!foto || !fotoContentType) {
      return '';
    }

    return `data:${fotoContentType};base64,${foto}`;
  }

  private updateUserAvatarLocally(imageDataUrl: string): void {
    const userData = { ...this.getUserDataAuth() };
    userData.urlImg = imageDataUrl;

    this.authentication.saveUserDataAuth(userData);
    this.authentication.saveCustomAvatar(userData.correo || '', imageDataUrl);
    this.authState.userDataAuth = this.authentication.getUserDataAuth();
  }

}
